import { useState, useRef, useImperativeHandle, forwardRef } from "react";
import { Input } from "@/components/ui/input";

/**
 * Editable cell input with Italian formatting:
 * - Shows formatted value (comma decimal) when not editing
 * - Select-all on focus for easy overwrite
 * - Formats on blur with proper decimals
 * - Supports `ref.focus()` from parent (via forwardRef + useImperativeHandle)
 *
 * @param {number} value - The numeric value
 * @param {function} onChange - Called with the new numeric value
 * @param {number} decimals - Number of decimal places (2 for prices, 0-1 for qty)
 * @param {boolean} disabled - Disable editing
 * @param {string} className - Additional CSS classes
 * @param {string} placeholder - Placeholder text
 */
export const EditableCell = forwardRef(function EditableCell(
  { value, onChange, decimals = 2, disabled, className = "", placeholder, ...props },
  ref,
) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef(null);

  // Espone .focus() (e l'elemento input nativo) al parent.
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    get input() { return inputRef.current; },
  }), []);

  const formatDisplay = (val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return placeholder || "-";
    if (Number.isInteger(num)) return String(num);
    // mostra fino a "decimals" cifre ma rimuovi zeri finali
    return num
      .toFixed(decimals)
      .replace(/\.?0+$/, "")
      .replace(".", ",");
  };

  const handleFocus = (e) => {
    setEditing(true);
    const num = parseFloat(value);
    if (isNaN(num)) {
      setEditValue("");
    } else if (Number.isInteger(num)) {
      setEditValue(String(num));
    } else {
      setEditValue(num.toFixed(decimals).replace(/\.?0+$/, "").replace(".", ","));
    }
    setTimeout(() => e.target.select(), 0);
  };

  const handleBlur = () => {
    setEditing(false);
    const parsed = parseFloat(editValue.replace(",", "."));
    if (!isNaN(parsed)) {
      onChange(Math.round(parsed * Math.pow(10, decimals)) / Math.pow(10, decimals));
    }
  };

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.,\-]/g, "");
    setEditValue(raw);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={editing ? editValue : formatDisplay(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  );
});
